import type { IPagingFilter } from "@frui.ts/data";
import { bound } from "@frui.ts/helpers";
import { watchBusy } from "@frui.ts/screens";
import type { EntityNames } from "config";
import RefundForRefundOfRefundResponse from "entities/refundForRefundOfRefundResponse";
import TransferRefundForRefundOfRefundPaymentTemplate from "entities/transferRefundForRefundOfRefundPaymentTemplate";
import type RefundForRefundOfRefundPayment from "entities/refundForRefundOfRefundPaymentResponse";
import TransferType from "entities/transferType";
import { computed } from "mobx";
import type RefundForRefundOfRefundsRepository from "repositories/refundForRefundOfRefundsRepository";
import type { UpdatedEvent } from "services/eventBus";
import type EventBus from "services/eventBus";
import {
  domesticTransferRefundUpdated,
  foreignTransferRefundUpdated,
  SEPATransferRefundUpdated,
  transferRefundForRefundOfRefundPaymentTypeChanged,
} from "services/eventBus";
import type EnumService from "services/enumService";
import type LocalizationService from "services/localizationService";
import type OptionsService from "services/optionsService";
import type RefundsApprovalService from "services/refundsApprovalService";
import type AppRouter from "services/router";
import type SecurityService from "services/securityService";
import { getEntityName } from "utils/entityName";
import type CountrySelectViewModel from "viewModels/countrySelectViewModel";
import type AlertsViewModel from "../../../alertsViewModel";
import EditableRefundForRefundOfRefundDetailViewModel from "../detail/editableRefundForRefundOfRefundDetailViewModel";
import TransferPaymentType from "entities/transferPaymentType";
import type RefundForRefundOfRefundDetailResponse from "entities/refundForRefundOfRefundDetailResponse";
import FeeType from "entities/feeType";
import type PriceListService from "services/priceListService";
import { emptyValue } from "utils/emptyValue";

export default abstract class TransferRefundForRefundOfRefundsDetailViewModel extends EditableRefundForRefundOfRefundDetailViewModel<
  RefundForRefundOfRefundDetailResponse,
  RefundForRefundOfRefundResponse
> {
  readonly modelName: EntityNames;

  @computed
  get showSEPAWarning() {
    const countryCode = this.item.refundPayment.counterpartyBank?.countryCode ?? "";
    const isSepaMember = this.enumService.values("countries").find(item => item.code === countryCode)?.isSepaMember ?? false;

    return this.item.refundPayment.transferType === TransferPaymentType.Foreign && isSepaMember && this.isPaymentTypeEditable;
  }

  constructor(
    protected itemId: string,
    protected pagingFilter: IPagingFilter,
    protected refundType: TransferType,
    protected localizationService: LocalizationService,
    protected appRouter: AppRouter,
    protected approvalService: RefundsApprovalService,
    protected repository: RefundForRefundOfRefundsRepository,
    protected optionsService: OptionsService,
    protected securityService: SecurityService,
    protected eventBus: EventBus,
    protected countrySelectViewModelFactory: ReturnType<typeof CountrySelectViewModel.Factory>,
    protected priceListService: PriceListService,
    protected enumService: EnumService,
    public alertsViewModel: AlertsViewModel
  ) {
    super(
      itemId,
      pagingFilter,
      localizationService,
      appRouter,
      approvalService,
      repository,
      optionsService,
      securityService,
      eventBus,
      countrySelectViewModelFactory,
      priceListService,
      enumService,
      alertsViewModel
    );
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    this.modelName = getEntityName(RefundForRefundOfRefundResponse)!;
  }

  @watchBusy
  public fetchData() {
    const { limit, offset } = this.pagingFilter;

    const filter = { transferType: TransferPaymentType.Unspecified };
    switch (this.refundType) {
      case TransferType.Domestic:
        filter.transferType = TransferPaymentType.Domestic;
        break;
      case TransferType.Sepa:
        filter.transferType = TransferPaymentType.Sepa;
        break;
      case TransferType.Foreign:
        filter.transferType = TransferPaymentType.Foreign;
        break;
    }

    return this.repository.search({ limit, offset }, filter);
  }

  protected fetchDetail(id: string): Promise<RefundForRefundOfRefundDetailResponse> {
    return this.repository.get(id);
  }

  protected updatePayment(id: string, data: RefundForRefundOfRefundPayment): Promise<any> {
    return this.repository.updateRefundPayment(id, data);
  }

  @bound
  @watchBusy
  onRefundTypeChanged() {
    this.eventBus.publish(
      transferRefundForRefundOfRefundPaymentTypeChanged({
        newTransferType: this.item.refundPayment.transferType || TransferPaymentType.Unspecified,
        originalTransferType: this.originalPaymentType,
        refundForRefundOfRefundId: this.itemId,
      })
    );
    this.requestClose();
  }

  @bound
  protected async onRefundSaved() {
    if (this.originalPaymentType !== this.item.refundPayment.transferType) {
      return this.onRefundTypeChanged();
    }

    switch (this.item.refundPayment.transferType) {
      case TransferPaymentType.Sepa:
        this.eventBus.publish(SEPATransferRefundUpdated());
        break;
      case TransferPaymentType.Domestic:
        this.eventBus.publish(domesticTransferRefundUpdated());
        break;
      case TransferPaymentType.Foreign:
        this.eventBus.publish(foreignTransferRefundUpdated());
        break;
    }

    this.eventBus.publish<UpdatedEvent<RefundForRefundOfRefundPayment>>({
      type: `transferRefundPayment.updated`,
      payload: { transferRefundPayment: this.item.refundPayment },
    });

    await this.loadDetail();
  }

  @computed
  get chargeRefundFee() {
    return (this.item.fees ?? []).find(fee => fee.feeType === FeeType.ChargeRefundFee)?.amount ?? 0;
  }

  @computed
  get bankFees() {
    return (this.item.fees ?? []).reduce((sum: number, fee) => {
      if (fee.feeType === FeeType.ForeignPaymentCost || fee.feeType === FeeType.ForeignPaymentToSepaCountriesCost) {
        sum += fee.amount;
      }
      return sum;
    }, 0);
  }

  translateRefundType(type: TransferType) {
    return this.localizationService.translateEnum("transferType", type);
  }

  translateRefundReason(refundPaymentTemplateId: string) {
    return (
      this.optionsService
        .entityOptions(TransferRefundForRefundOfRefundPaymentTemplate)
        .find(option => option.id === refundPaymentTemplateId)?.label ?? emptyValue
    );
  }
}
